import type { CaseManifestV1 } from "@cold-case/shared/case-manifest";
import { MIN_EVIDENCE_FOR_ACCUSATION } from "@cold-case/shared/constants";
import { resolveCaseDisplayTitle } from "@cold-case/shared/manifest-display";

import { substituteDialog } from "./dialog-parser.js";

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export type CaseBriefHtmlOptions = {
  minEvidence?: number;
  footerHtml?: string;
};

export function caseBriefDisplayTitle(manifest: CaseManifestV1): string {
  return resolveCaseDisplayTitle(manifest);
}

/** Player-facing Case File → Brief tab (no internal ids or operator jargon). */
export function renderCaseBriefHtml(
  manifest: CaseManifestV1,
  playerNames: string[],
  opts: CaseBriefHtmlOptions = {}
): string {
  const minEvidence = opts.minEvidence ?? MIN_EVIDENCE_FOR_ACCUSATION;
  const victim = manifest.victim;
  const victimName = victim?.name?.trim() || "the victim";
  const cause = victim?.cause_of_death?.trim() || "unknown cause";
  const hookRaw = substituteDialog(
    manifest.crime_scene?.narrative_hook,
    playerNames,
    victimName
  );
  const hook = hookRaw
    ? `<p>${escapeHtml(hookRaw)}</p>`
    : "<p>Review the neighborhood and follow what the evidence suggests.</p>";
  const pivotalCount = manifest.accusation_schema.required_evidence?.length ?? 0;
  const accusation =
    pivotalCount > 0
      ? `When you are ready to accuse someone, open the <strong>Accuse</strong> tab, name a suspect you have met in the world. Strong cases usually tie together several discoveries from buildings and conversations.`
      : `When you are ready to accuse someone, open the <strong>Accuse</strong> tab, and start a vote with your fellow detectives. Strong cases usually tie together several discoveries from buildings and conversations.`;

  const title = escapeHtml(caseBriefDisplayTitle(manifest));

  return `<p class="case-brief-title">${title}</p>
<p><strong>Victim:</strong> ${escapeHtml(victimName)} — ${escapeHtml(cause)}</p>
${hook}
<p>Review the <strong>Timeline</strong> in your case files for more information.</p>
<p>${accusation}</p>
${opts.footerHtml ?? ""}`;
}
